import { all, call, delay, put, select, take } from 'redux-saga/effects'

import { updateSingleDocumentMapping } from '../effects/updateSingleDocumentMapping'
import { convertOpeningGroupsToDrawableLocations } from './data-prep/convertOpeningsToDrawableLocations'
import { getMeasurementLabelForToolObject } from './handleCreateMeasurements'
import { isTemplatePage, isUnclassifiedPage } from './reviewPlan'
import { regenerateMeasurementsByProjectAndDocumentIds } from './saveUpdatedCalibrationFactor'
import { updateJoistDataForGroup } from './updateDrawableCoordinates'
import { FETCH_OPENING_GROUPS_SUCCESS } from '../../../actions/drawable'
import { loadProjectBuildings, updateProjectContextMarkups } from '../../../api/projects-api'
import { fetchOpeningGroupsByProjectId, updateDocumentMapping } from '../../../api/takeoff-api'
import { OpeningGroupAPI } from '../../../models/activeDrawable'
import { DocumentChunk } from '../../../models/documentChunk'
import { DocumentMapping } from '../../../models/documentMapping'
import { IMasterSetPlanOption, MASTER_SET_PLAN_ENUMS } from '../../../models/masterSetPlan'
import { Building } from '../../../models/projectBuilding'
import { IToolObject } from '../../../models/tool'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { MasterSetPlanRegion, Workspace } from '../../lib/toolBoxes/2D'
import { preparedDrawablesSuccess } from '../../slices/2D'
import { setBuildings } from '../../slices/buildings'
import { setInfoModalData, updateToolbarMessage } from '../../slices/common'
import {
    resetDocumentChunkCalibration,
    selectDocumentChunks,
    selectDocumentMappings,
    updateActiveChunk,
    updateDocumentMappings,
} from '../../slices/documents'
import { selectMasterSetPlanOptions, updateMasterSetPlanOptions } from '../../slices/masterSetPlan'
import { selectProjectId } from '../../slices/project'
import { resetRegionFormState } from '../../slices/region'
import { selectMeasurementToolObjects, updateToolObjectByToolId } from '../../slices/tools'
import { ALL_CHANGES_SAVED, IMUP2DDrawableLocation, SAVING_MESSAGE, TOOLBAR_MESSAGE_TIMER } from '../../types'

export function updateDocumentMappingDataToConformToAPI(documentMapping: DocumentMapping) {
    return {
        ...documentMapping,
        created_at: undefined,
        updated_at: undefined,
        deleted_at: undefined,
    } as DocumentMapping
}

export function* updateLabelForToolObjectInStore(
    toolObject: IToolObject,
    chunk: DocumentChunk,
    mapping: DocumentMapping
) {
    const newLabel: string = yield call(getMeasurementLabelForToolObject, toolObject, chunk, mapping)

    try {
        const projectId: number = yield select(selectProjectId)

        yield call(updateProjectContextMarkups, projectId, toolObject.document_chunk_id, {
            ...toolObject,
            settings: {
                ...toolObject.settings,
                label: newLabel,
            },
        })
        yield put(
            updateToolObjectByToolId({
                ...toolObject,
                settings: {
                    ...toolObject.settings,
                    label: newLabel,
                },
            })
        )
    } catch (error) {
        console.error('Failed to update markup label:', error)
    }
}

export function* handleUpdateMeasurementToolObjectLabels(chunk: DocumentChunk, mapping: DocumentMapping) {
    if (mapping.document_chunk_id !== chunk.id) return

    const toolObjects: IToolObject[] = yield select(selectMeasurementToolObjects)

    yield all(
        toolObjects.map((object) => {
            if (object.document_chunk_id === chunk.id) {
                return call(updateLabelForToolObjectInStore, object, chunk, mapping)
            }
        })
    )
}

function shouldUpdateToolBarMessage(documentMapping: DocumentMapping) {
    return documentMapping.additional_data.parentDocumentMapping || isTemplatePage(documentMapping)
}

export function* regenerateJoistLines(openingGroups: OpeningGroupAPI[], projectId: number, documentChunkId: number) {
    const floorRoofSystemOnAPage = openingGroups.filter((group) => {
        return (
            group.type === DRAWABLE_TYPES.FLOOR_SYSTEM ||
            group.type === DRAWABLE_TYPES.FLOOR_SYSTEMS ||
            group.type === DRAWABLE_TYPES.ROOF_SYSTEM ||
            group.type === DRAWABLE_TYPES.ROOF_SYSTEMS
        )
    })

    if (!floorRoofSystemOnAPage.length) return

    // For each floor/roof system group, check if it has joist lines on the rescaled page and regenerate
    // them. A group spans every page its selections are drawn on, so only its openings on this page
    // are inspected and sent for regeneration.
    for (const group of floorRoofSystemOnAPage) {
        const openingsOnPage = group.openings.filter((opening) =>
            opening.opening_locations.some((location) => location.document_chunk_id === documentChunkId)
        )

        const haveJoistLines = openingsOnPage.some((opening) =>
            opening.opening_locations.some((location) => location?.additional_data?.raw_joist_length)
        )

        const haveDoubleJoist = openingsOnPage.some((opening) =>
            opening.opening_locations.some((location) => location?.additional_data?.is_double_joist)
        )

        if (haveDoubleJoist) {
            yield put(
                setInfoModalData({
                    visible: true,
                    title: 'Double Joist',
                    body: 'This page contains Double Joist, changing Scale removed the Double Joist.',
                })
            )
        }

        if (haveJoistLines) {
            yield call(
                updateJoistDataForGroup,
                projectId,
                group.id,
                openingsOnPage.map((opening) => opening.id)
            )

            // Wait for the toolbar message to be cleared, which signals completion
            // The redrawJoistLinesOnUpdate saga sets updateToolbarMessage(null) in its finally block
            yield take((action) => action.type === 'common/updateToolbarMessage' && action.payload === null)
        }
    }
}

export function* handleUpdateSingleDocumentMapping2D({ payload }: ReturnType<typeof updateSingleDocumentMapping>) {
    try {
        const projectId: number = yield select(selectProjectId)
        const documentChunks: DocumentChunk[] | null = yield select(selectDocumentChunks)
        const documentMappings: DocumentMapping[] = yield select(selectDocumentMappings)
        const masterSetOptions: IMasterSetPlanOption[] = yield select(selectMasterSetPlanOptions)
        const mappingChunk = documentChunks?.find((chunk) => chunk.id === payload.document_chunk_id)

        const manager: PaperManager | null = yield call(managers.get2DManager)

        if (payload.building_id !== -1) {
            yield call(updateDocumentMapping, projectId, updateDocumentMappingDataToConformToAPI(payload))
        }

        if (shouldUpdateToolBarMessage(payload)) {
            yield put(updateToolbarMessage(SAVING_MESSAGE))
        }

        // Update the mapping in the store
        yield put(updateDocumentMappings([payload]))

        // BE prunes / migrates wall_sheathing_size_<floor> for affected buildings inside the
        // UpdateDocumentMapping transaction; refetch so the store reflects the new settings.
        if (payload.building_id !== -1) {
            const refreshedBuildings: Building[] = yield call(loadProjectBuildings, projectId)

            yield put(setBuildings({ buildings: refreshedBuildings }))
        }

        yield put(
            updateMasterSetPlanOptions(
                masterSetOptions.map((option) => {
                    if (option.id === payload.id) {
                        return {
                            ...option,
                            name: payload.page_name ?? '',
                            scale: payload.scale_factor,
                            type: payload.type,
                        }
                    }

                    return option
                })
            )
        )

        const foundDocumentMapping: DocumentMapping | undefined = yield documentMappings.find(
            (documentMapping) => documentMapping.id === payload.id
        )

        if (
            mappingChunk &&
            !foundDocumentMapping?.is_option &&
            foundDocumentMapping &&
            foundDocumentMapping.scale_factor !== payload.scale_factor &&
            manager
        ) {
            yield put(resetDocumentChunkCalibration({ documentChunkId: mappingChunk.id }))

            yield regenerateMeasurementsByProjectAndDocumentIds(
                manager,
                projectId,
                foundDocumentMapping.document_chunk_id
            )
        }

        // Fetch opening groups from api
        const openingGroups: OpeningGroupAPI[] = yield call(fetchOpeningGroupsByProjectId, projectId)

        // Original IMUP: pass api response to original IMUP reducer
        yield put({ type: FETCH_OPENING_GROUPS_SUCCESS, payload: openingGroups })

        // Convert opening groups to drawable locations with appropriate metadata
        const drawableLocations: IMUP2DDrawableLocation[] = yield call(
            convertOpeningGroupsToDrawableLocations,
            openingGroups
        )

        // Retrieve the unique set of document chunk IDs for all locations
        const documentChunkIds: number[] = [...new Set(drawableLocations.map((location) => location.document_chunk_id))]

        // Provide drawable locations and unique document chunk IDs to the store
        yield put(preparedDrawablesSuccess({ drawableLocations, documentChunkIds }))

        if (manager) {
            const [workspaceTool, masterSetPlanRegionTool]: [Workspace, MasterSetPlanRegion] = yield call(
                manager.getTools,
                [Workspace.NAME, MasterSetPlanRegion.NAME]
            )

            const optionItems: paper.Item[] = yield call(
                workspaceTool.getItemsWithCriteria,
                'data',
                (data) => data.masterSetPlanId === payload.id
            )

            if (optionItems.length !== 0) {
                if (optionItems.length > 1) throw 'More than one option path found something is wrong'

                const labelItem: paper.PointText | null = yield call(
                    workspaceTool.getItemWithPaperId<paper.PointText>,
                    optionItems[0].data.labelId
                )

                const labelBackground: paper.Item | null = yield call(
                    workspaceTool.getItemWithPaperId,
                    optionItems[0].data.labelBackgroundId
                )

                if (!labelItem || !labelBackground) return

                yield call(
                    masterSetPlanRegionTool.updatePathColorAndText.bind(masterSetPlanRegionTool),
                    optionItems[0],
                    labelBackground,
                    labelItem,
                    payload.is_option
                        ? MASTER_SET_PLAN_ENUMS.MASTER_SET_PLAN_OPTION
                        : MASTER_SET_PLAN_ENUMS.MASTER_SET_PLAN_BASE_HOME
                )
            }
        }

        // If the scale factor changed, check for floor/roof system/s with joist lines and regenerate them
        if (foundDocumentMapping && foundDocumentMapping.scale_factor !== payload.scale_factor) {
            yield call(
                regenerateJoistLines,
                openingGroups.filter(
                    (group) =>
                        group.openings.filter(
                            (op) =>
                                op.opening_locations.filter(
                                    (loc) => loc.document_chunk_id === foundDocumentMapping.document_chunk_id
                                ).length > 0
                        ).length > 0
                ),
                projectId,
                foundDocumentMapping.document_chunk_id
            )
        }

        const isMasterSetPlanOrTemplatePage =
            payload.additional_data.parentDocumentMapping || isTemplatePage(payload) || isUnclassifiedPage(payload)

        if (!isMasterSetPlanOrTemplatePage) {
            yield put(updateActiveChunk(null))
        }
        yield put(resetRegionFormState())
    } catch (error) {
        yield call(console.error, error)
    } finally {
        if (shouldUpdateToolBarMessage(payload)) {
            yield put(updateToolbarMessage(ALL_CHANGES_SAVED))
        }

        yield put(delay(TOOLBAR_MESSAGE_TIMER))
        yield put(updateToolbarMessage(null))
    }
}
